import {
  Alert,
  Button,
  DrawerBody,
  DrawerFooter,
  LoadingButton,
  Typography,
} from '@one-thd/sui-atomic-components';
import { useLazyDataModel } from '@thd-nucleus/data-sources';
import React, { lazy, Suspense, useEffect, useState, useRef } from 'react';
import { track } from '../../../../../analytics/analytics';
import {
  INSTANT_CHECKOUT_ENROLLMENT_SUCCESS as ANALYTICS_INSTANT_CHECKOUT_ENROLLMENT_SUCCESS,
  INSTANT_CHECKOUT_BUTTON_SUBMISSION,
  INSTANT_CHECKOUT_SUCCESS,
  POST_PAGE_ERROR,
} from '../../../../../constants/analytics';
import {
  INSTANT_CHECKOUT_ENROLLMENT_SUCCESS,
  INSTANT_CHECKOUT_ADDRESS_UPDATE_SUCCESS,
  INSTANT_CHECKOUT_PAYMENT_UPDATE_SUCCESS,
} from '../../../../../constants/response-success-messages';
import { useDrawer } from '../../../../../hooks/use-drawer';
import { useInstantCheckoutStatus } from '../../../../../hooks/use-instant-checkout-status';
import { usePayments } from '../../../../../hooks/use-payments';
import { TAlertStatus, TAlertType } from '../../../../../models/common/alert';
import {
  orangePayToken,
  updateInstantCheckoutSettings,
} from '../../../../../models/graphQLModels';
import { IPaymentInput } from '../../../../../models/payments';
import PaymentCard from '../../../../shared/PaymentCard';
import { InstantCheckoutDrawerHeader } from '../../../InstantCheckoutDrawerHeader';
import { SkeletonLoader } from '../../../../shared/SkeletonLoader';
import {
  ERR_GENERIC,
  ERR_INVALID_PAYMENT,
  ERR_REDIRECT,
} from '../../../../../constants/response-error-messages';
import { useAddress } from '../../../../../hooks/use-address';
import { useViews } from '../../../../../hooks/use-views';
import {
  EDIT_DELIVERY_DETAILS,
  SETUP_HOW_YOU_PAY,
  CHANGE_WHERE_YOU_GET_IT,
  SETUP_WHERE_YOU_GET_IT,
} from '../../../../../models/views';

import {
  createNewRelicActions,
  logNewRelicAction,
} from '../../../../../utils/newrelic';
import { TOrangePaySuccessResponse } from '../../../../../utils/orangePay';
import {
  getChannelID,
  getCustomerIp,
  redirectToSignIn,
} from '../../../../../utils/general';
import {
  getIsAuthError,
  isInvalidPaymentAuthAttempt,
} from '../../../../../constants/error-codes';
import { shouldShowCardExpirationWithBrand } from '../../../../../utils/payments';
import { getCookie } from '../../../../../utils/cookies';

const OrangePayCVVOnlyDynamicImport = lazy(
  () => import('../../../../shared/OrangePayContent'),
);
export const SecurityCodeCVVView = () => {
  const {
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    refetchPayments,
    defaultPayment,
  } = usePayments();

  const { currentView } = useViews();
  const { hideDrawer } = useDrawer();
  const { refetchAddresses, selectedAddress, defaultAddress } = useAddress();
  const {
    setAlertMessage,
    refetchProfile,
    userId,
    customerAccountId,
    profile,
  } = useInstantCheckoutStatus();
  const { flowName } = useViews();
  const actions = createNewRelicActions(currentView);
  const card = selectedPaymentMethod || defaultPayment;

  const [loading, setLoading] = useState(false);
  const [isMicroUILoading, setIsMicroUILoading] = useState(true);
  const [sendOrangePayRemoteAuth, setSendOrangePayRemoteAuth] = useState(false);
  const [, setIsMicroUIFormValid] = useState(false);
  const [isOrangePayLoaded, setIsOrangePayLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState<null | string>(null);

  const showUpdateMsg =
    card?.cardDetails?.isExpired || !card?.billingAddress?.id;

  const [callUpdateInstantCheckout, data] = useLazyDataModel(
    'updateInstantCheckoutSettings',
  );

  const handleAddAPIError = (errors: []) => {
    if (isInvalidPaymentAuthAttempt(errors)) {
      assignErrorMessage(ERR_INVALID_PAYMENT.message);
    } else {
      assignErrorMessage(ERR_GENERIC.message);
    }
  };

  const {
    called,
    loading: updateLoading,
    error: updateError,
    data: updateResult,
  } = data;

  const hasUpdated = useRef(false);

  useEffect(() => {
    if (
      updateResult &&
      called &&
      !updateLoading &&
      !updateError &&
      !hasUpdated.current
    ) {
      hasUpdated.current = true;
      setLoading(false);
      refetchPayments();
      refetchAddresses();
      refetchProfile();

      if (
        flowName === EDIT_DELIVERY_DETAILS ||
        flowName === CHANGE_WHERE_YOU_GET_IT ||
        flowName === SETUP_WHERE_YOU_GET_IT
      ) {
        const action = flowName === EDIT_DELIVERY_DETAILS ? 'edit' : 'select';
        track(INSTANT_CHECKOUT_SUCCESS, {
          action,
          item: 'delivery address',
        });
        setAlertMessage({
          alertType: TAlertType.IC_ADDRESS_UPDATE,
          alertStatus: TAlertStatus.SUCCESS,
          text: INSTANT_CHECKOUT_ADDRESS_UPDATE_SUCCESS.message,
        });
      } else if (
        (flowName === SETUP_WHERE_YOU_GET_IT ||
          flowName === CHANGE_WHERE_YOU_GET_IT) &&
        updateError
      ) {
        logNewRelicAction(actions.UPDATE_PROFILE, updateError);
      } else if (flowName === SETUP_HOW_YOU_PAY) {
        track(INSTANT_CHECKOUT_SUCCESS, {
          action: 'select',
          item: 'payment method',
        });
        setAlertMessage({
          alertType: TAlertType.IC_PAYMENT_UPDATE,
          alertStatus: TAlertStatus.SUCCESS,
          text: INSTANT_CHECKOUT_PAYMENT_UPDATE_SUCCESS.message,
        });
      } else {
        track(ANALYTICS_INSTANT_CHECKOUT_ENROLLMENT_SUCCESS, {
          action: 'select',
          item: 'payment method',
        });
        setAlertMessage({
          alertType: TAlertType.TOP,
          alertStatus: TAlertStatus.SUCCESS,
          text: INSTANT_CHECKOUT_ENROLLMENT_SUCCESS.message,
        });
      }
      logNewRelicAction(actions.ENROLLMENT);
      hideDrawer();
    } else if (updateError) {
      logNewRelicAction(actions.ENROLLMENT, updateError);
      if (getIsAuthError(updateError)) {
        setAlertMessage({
          alertType: TAlertType.GENERAL,
          alertStatus: TAlertStatus.INFO,
          text: ERR_REDIRECT.message,
        });
        redirectToSignIn();
      }

      if (
        updateError?.graphQLErrors &&
        Array.isArray(updateError.graphQLErrors)
      ) {
        handleAddAPIError(updateError.graphQLErrors);
      } else {
        assignErrorMessage(ERR_GENERIC.message);
      }
      setLoading(false);
      track(POST_PAGE_ERROR, {
        pageSection: 'security code overlay',
        message: ERR_GENERIC.message,
        code: 'n/a',
        type: 'error',
        displayType: 'inline',
      });
    }
  }, [
    updateResult,
    updateLoading,
    called,
    updateError,
    refetchPayments,
    refetchAddresses,
    refetchProfile,
    flowName,
    actions.ENROLLMENT,
    hideDrawer,
    setAlertMessage,
  ]);

  const handleSuccess = (cardData: TOrangePaySuccessResponse) => {
    if (card) {
      const cardWithoutExpired = card;
      if (cardWithoutExpired.cardDetails) {
        delete cardWithoutExpired.cardDetails.isExpired;
      }
      const showExpiration = shouldShowCardExpirationWithBrand(
        card?.cardDetails?.cardBrand?.name,
      );

      const paymentCard: IPaymentInput = {
        ...cardWithoutExpired,
        cardDetails: {
          ...cardWithoutExpired.cardDetails,
          ...cardData.cardDetails,
          expirationMonth: cardData.cardDetails.expirationMonth,
          expirationYear: cardData.cardDetails.expirationYear,
          cardBrand: {
            ...cardWithoutExpired.cardDetails.cardBrand,
            paymentMethodCode: cardData.paymentMethodCode,
            reportPaymentMethodCode: cardData.reportPaymentMethodCode,
          },
        },
        cardReference: cardData.cardReference,
        customerIP: getCustomerIp(),
        forterToken: getCookie('forterToken') || '',
        type: 'CREDIT_CARD',
        isInstantCheckoutDefault: true,
      };

      // we don't send expiration details with cards that don't have expiration dates (HD Consumer, HD Commerical, PROX)

      if (!showExpiration) {
        delete paymentCard.cardDetails.expirationMonth;
        delete paymentCard.cardDetails.expirationYear;
      }

      delete paymentCard.cardDetails.authorizedBuyerID;
      delete paymentCard.cardDetails.reportPaymentMethodCode;
      delete paymentCard.cardDetails.paymentMethodCode;

      callUpdateInstantCheckout({
        variables: {
          userId,
          customerAccountId,
          channelId: getChannelID(),
          address: selectedAddress || defaultAddress,
          paymentCard,
          account: profile,
        },
        ssr: false,
        context: {
          withAuth: true,
        },
      });
    }
  };

  const formSubmit = (event: any) => {
    event.preventDefault();

    // if card does not have address or is expired do not allow to continue
    // or if orange pay never loaded
    if (showUpdateMsg || !isOrangePayLoaded) {
      return;
    }
    setSendOrangePayRemoteAuth(true);

    if (flowName === EDIT_DELIVERY_DETAILS) {
      track(INSTANT_CHECKOUT_BUTTON_SUBMISSION, {
        overlayType: 'edit delivery details security code',
        action: 'save changes',
      });
    } else if (
      flowName === CHANGE_WHERE_YOU_GET_IT ||
      flowName === SETUP_WHERE_YOU_GET_IT
    ) {
      logNewRelicAction(actions.UPDATE_PROFILE);
      track(INSTANT_CHECKOUT_BUTTON_SUBMISSION, {
        overlayType: 'select delivery address security code',
        action: 'save changes',
      });
    } else {
      track(INSTANT_CHECKOUT_BUTTON_SUBMISSION, {
        overlayType: 'select payment method',
        action: 'save changes',
      });
    }

    setLoading(true);
  };

  const assignErrorMessage = (message: string | null) => {
    setErrorMessage(message);
  };

  const handleClose = () => {
    hideDrawer();
    if (flowName === EDIT_DELIVERY_DETAILS) {
      track(INSTANT_CHECKOUT_BUTTON_SUBMISSION, {
        overlayType: 'edit delivery details security code',
        action: 'cancel',
      });
    } else if (
      flowName === CHANGE_WHERE_YOU_GET_IT ||
      flowName === SETUP_WHERE_YOU_GET_IT
    ) {
      track(INSTANT_CHECKOUT_BUTTON_SUBMISSION, {
        overlayType: 'select delivery address security code',
        action: 'cancel',
      });
    }
    track(INSTANT_CHECKOUT_BUTTON_SUBMISSION, {
      overlayType: 'select payment method',
      action: 'cancel',
    });
  };

  return (
    <>
      {isMicroUILoading ? <SkeletonLoader /> : null}
      <div
        hidden={isMicroUILoading}
        data-testid="confirm-payment-method-section"
      >
        <InstantCheckoutDrawerHeader
          title="Confirm Payment Method"
          data-testid="drawer-header"
          onClose={handleClose}
        />

        <DrawerBody data-testid="confirm-payment-method-drawer">
          <form
            onSubmit={formSubmit}
            className="sui-relative sui-pt-1"
            id="cvv-form"
          >
            <PaymentCard
              card={card}
              selectedPaymentMethod={card}
              onSelect={setSelectedPaymentMethod}
            />

            <div hidden={showUpdateMsg}>
              {typeof window !== 'undefined' ? (
                <Suspense fallback={<></>}>
                  <OrangePayCVVOnlyDynamicImport
                    setIsMicroUILoading={setIsMicroUILoading}
                    assignErrorMessage={assignErrorMessage}
                    setLoading={setLoading}
                    sendOrangePayRemoteAuth={sendOrangePayRemoteAuth}
                    setSendOrangePayRemoteAuth={setSendOrangePayRemoteAuth}
                    setIsMicroUIFormValid={setIsMicroUIFormValid}
                    isMicroUILoading={isMicroUILoading}
                    isCVVOnly
                    setIsOrangePayLoaded={setIsOrangePayLoaded}
                    isOrangePayLoaded={isOrangePayLoaded}
                    successHandler={handleSuccess}
                    card={card}
                    view={currentView}
                  />
                </Suspense>
              ) : (
                <></>
              )}
            </div>

            {errorMessage &&
              (errorMessage === ERR_INVALID_PAYMENT.message ? (
                <Alert status="error">
                  <Typography>
                    Something went wrong. Correct one or more of these possible
                    issues:
                  </Typography>
                  <Typography component="div">
                    <ul className="sui-mt-4 sui-list-disc sui-list-outside sui-pl-7 ">
                      <li>Card details are not accurate</li>
                      <li>Card was declined by bank/card user</li>
                    </ul>
                  </Typography>
                </Alert>
              ) : (
                <div className="sui-mt-10">
                  <Alert status="error">{errorMessage}</Alert>
                </div>
              ))}
          </form>
        </DrawerBody>

        <DrawerFooter>
          <div className="sui-flex sui-gap-2">
            <Button
              data-testid="cancel-button"
              fullWidth
              variant="secondary"
              onClick={handleClose}
            >
              Cancel
            </Button>

            <LoadingButton
              loading={loading}
              data-testid="save-button"
              fullWidth
              variant="primary"
              type="submit"
              form="cvv-form"
            >
              Save Changes
            </LoadingButton>
          </div>
        </DrawerFooter>
      </div>{' '}
    </>
  );
};

SecurityCodeCVVView.displayName = 'SecurityCodeCVVView';
SecurityCodeCVVView.dataModel = {
  updateInstantCheckoutSettings,
  orangePayToken,
};
